import { NavLink, useSearchParams } from 'react-router';
import type { ReactNode } from 'react';

export interface TabItem {
  key: string;
  label: ReactNode;
  /** a route segment (NavLink) … */
  to?: string;
  count?: number;
}

/** Route-bound tabs: the active tab is a URL segment (`to`) or a `?<param>=` value; switching changes the URL only. */
export function Tabs({ items, param, value, onChange, segmented, ariaLabel }: { items: TabItem[]; param?: string; value?: string; onChange?: (key: string) => void; segmented?: boolean; ariaLabel?: string }) {
  const [params, setParams] = useSearchParams();
  const current = param ? (params.get(param) ?? items[0]?.key) : value;
  return (
    <div className={segmented ? 'segmented' : 'tabs'} role="tablist" aria-label={ariaLabel}>
      {items.map((item) => {
        const label = (
          <>
            {item.label}
            {item.count !== undefined && <span className="muted"> {item.count}</span>}
          </>
        );
        if (item.to) {
          return (
            <NavLink key={item.key} to={item.to} end className={({ isActive }) => `tab ${isActive ? 'active' : ''}`} role="tab">
              {label}
            </NavLink>
          );
        }
        const selected = current === item.key;
        return (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={selected}
            className="tab"
            onClick={() => {
              if (param) {
                const next = new URLSearchParams(params);
                if (item.key === items[0]?.key) next.delete(param);
                else next.set(param, item.key);
                setParams(next, { replace: true });
              }
              onChange?.(item.key);
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
